import {
  ConversationListResponseSchema,
  ConversationResponseSchema,
  MessageListResponseSchema,
  MessageResponseSchema,
  CallListResponseSchema,
  CallResponseSchema,
  LeadListResponseSchema,
  LeadResponseSchema,
  ContactListResponseSchema,
  ContactResponseSchema,
  type MessageSendBody,
  type ConversationAssignBody,
  type LeadUpdateStageBody,
  type LeadUpdateQualificationBody,
  type ConversationListQuery,
  type LeadListQuery,
  type CallListQuery,
} from '../../src/shared/comms';
import { apiGet, apiPost, apiPatch } from './client';

/**
 * List endpoints all speak the shared paginated envelope
 * (`ListResponse<T>` in src/shared/comms.ts): `{ items, nextCursor }`.
 * These schemas previously invented per-domain key names, which parsed
 * nothing the Worker actually returns and broke every list view.
 */

// ── Query keys ────────────────────────────────────────────────────────────

export const commsKeys = {
  conversations: {
    all: (slug: string) => ['conversations', slug] as const,
    list: (slug: string, status?: string) =>
      ['conversations', slug, 'list', status ?? 'all'] as const,
    detail: (slug: string, id: string) => ['conversations', slug, id] as const,
  },
  messages: {
    list: (slug: string, conversationId: string) => ['messages', slug, conversationId] as const,
  },
  calls: {
    all: (slug: string) => ['calls', slug] as const,
    list: (slug: string, missedOnly?: boolean) =>
      ['calls', slug, 'list', missedOnly ? 'missed' : 'all'] as const,
    detail: (slug: string, id: string) => ['calls', slug, id] as const,
  },
  leads: {
    all: (slug: string) => ['leads', slug] as const,
    list: (slug: string, stage?: string) => ['leads', slug, 'list', stage ?? 'all'] as const,
    detail: (slug: string, id: string) => ['leads', slug, id] as const,
  },
  contacts: {
    all: (slug: string) => ['contacts', slug] as const,
    list: (slug: string) => ['contacts', slug, 'list'] as const,
    detail: (slug: string, id: string) => ['contacts', slug, id] as const,
  },
};

// ── Conversations ─────────────────────────────────────────────────────────

export function fetchConversations(
  slug: string,
  query?: ConversationListQuery,
  signal?: AbortSignal
): Promise<
  import('../../src/shared/comms').ListResponse<import('../../src/shared/comms').Conversation>
> {
  const params = new URLSearchParams();
  if (query?.status) params.set('status', query.status);
  if (query?.cursor) params.set('cursor', query.cursor);
  if (query?.limit) params.set('limit', String(query.limit));
  if (query?.assigneeId) params.set('assigneeId', query.assigneeId);
  const qs = params.toString();
  return apiGet(
    `/api/w/${slug}/conversations${qs ? `?${qs}` : ''}`,
    ConversationListResponseSchema,
    signal
  );
}

export function fetchConversation(
  slug: string,
  id: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').Conversation> {
  return apiGet(`/api/w/${slug}/conversations/${id}`, ConversationResponseSchema, signal);
}

export function assignConversation(
  slug: string,
  id: string,
  body: ConversationAssignBody
): Promise<import('../../src/shared/comms').Conversation> {
  return apiPost(`/api/w/${slug}/conversations/${id}/assign`, body, ConversationResponseSchema);
}

export function closeConversation(
  slug: string,
  id: string
): Promise<import('../../src/shared/comms').Conversation> {
  return apiPost(`/api/w/${slug}/conversations/${id}/close`, {}, ConversationResponseSchema);
}

// ── Messages ──────────────────────────────────────────────────────────────

export function fetchMessages(
  slug: string,
  conversationId: string,
  cursor?: string,
  signal?: AbortSignal
): Promise<
  import('../../src/shared/comms').ListResponse<import('../../src/shared/comms').Message>
> {
  const params = cursor ? `?cursor=${cursor}` : '';
  return apiGet(
    `/api/w/${slug}/conversations/${conversationId}/messages${params}`,
    MessageListResponseSchema,
    signal
  );
}

export function sendMessage(
  slug: string,
  conversationId: string,
  body: MessageSendBody
): Promise<import('../../src/shared/comms').Message> {
  return apiPost(
    `/api/w/${slug}/conversations/${conversationId}/messages`,
    body,
    MessageResponseSchema
  );
}

// ── Calls ─────────────────────────────────────────────────────────────────

export function fetchCalls(
  slug: string,
  query?: CallListQuery,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').ListResponse<import('../../src/shared/comms').Call>> {
  const params = new URLSearchParams();
  if (query?.missed) params.set('missed', 'true');
  if (query?.cursor) params.set('cursor', query.cursor);
  if (query?.limit) params.set('limit', String(query.limit));
  const qs = params.toString();
  return apiGet(`/api/w/${slug}/calls${qs ? `?${qs}` : ''}`, CallListResponseSchema, signal);
}

export function fetchCall(
  slug: string,
  id: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').Call> {
  return apiGet(`/api/w/${slug}/calls/${id}`, CallResponseSchema, signal);
}

// ── Leads ─────────────────────────────────────────────────────────────────

export function fetchLeads(
  slug: string,
  query?: LeadListQuery,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').ListResponse<import('../../src/shared/comms').Lead>> {
  const params = new URLSearchParams();
  if (query?.stage) params.set('stage', query.stage);
  if (query?.cursor) params.set('cursor', query.cursor);
  if (query?.limit) params.set('limit', String(query.limit));
  const qs = params.toString();
  return apiGet(`/api/w/${slug}/leads${qs ? `?${qs}` : ''}`, LeadListResponseSchema, signal);
}

export function fetchLead(
  slug: string,
  id: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').Lead> {
  return apiGet(`/api/w/${slug}/leads/${id}`, LeadResponseSchema, signal);
}

export function updateLeadStage(
  slug: string,
  id: string,
  body: LeadUpdateStageBody
): Promise<import('../../src/shared/comms').Lead> {
  return apiPost(`/api/w/${slug}/leads/${id}/stage`, body, LeadResponseSchema);
}

export function updateLeadQualification(
  slug: string,
  id: string,
  body: LeadUpdateQualificationBody
): Promise<import('../../src/shared/comms').Lead> {
  return apiPatch(`/api/w/${slug}/leads/${id}`, body, LeadResponseSchema);
}

// ── Contacts ──────────────────────────────────────────────────────────────

export function fetchContacts(
  slug: string,
  cursor?: string,
  signal?: AbortSignal
): Promise<
  import('../../src/shared/comms').ListResponse<import('../../src/shared/comms').Contact>
> {
  const params = cursor ? `?cursor=${cursor}` : '';
  return apiGet(`/api/w/${slug}/contacts${params}`, ContactListResponseSchema, signal);
}

export function fetchContact(
  slug: string,
  id: string,
  signal?: AbortSignal
): Promise<import('../../src/shared/comms').Contact> {
  return apiGet(`/api/w/${slug}/contacts/${id}`, ContactResponseSchema, signal);
}
